import http from './httpClient';
import { postUndo } from '../utils/ccsUndo';
import { postWithLockedRetry } from '../utils/ccsLockedRetry';

class StockInService {
  // Get all stock in records
  static async getStockInRecords(page = 1, limit = 50, status = null) {
    try {
  const params = new URLSearchParams({ page, limit, ...(status ? { status } : {}) });
  return await http.get(`/stock-in/v2?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching stock in records:', error);
      throw error;
    }
  }

  // Create new stock in record
  static async createStockIn(stockInData) {
    try {
  // A Locked month (continuous closing stock) offers a prior-month adjustment.
  return await postWithLockedRetry('/stock-in/v2', stockInData, 'prior_month_adjustment');
    } catch (error) {
      console.error('Error creating stock in record:', error);
      throw error;
    }
  }

  /**
   * Create a SERVER-side draft stock-in record.
   *
   * S7 — NO LIVE CALLER TODAY. "Save Draft" in StockInForm writes to localStorage; nothing
   * in the app calls this method. That matters because POST /stock-in/v2/draft sits behind
   * validateStockIn, which enforces the full submit contract — including vendor_phone.
   *
   * ⚠ IF SERVER DRAFTS ARE EVER WIRED TO THE UI, vendor_phone validation must be relaxed for
   * drafts: a half-typed number is the normal state of an unfinished bill and would 400
   * here. The same note is on the route (backend/routes/stockInV2.js, the /draft handler).
   */
  static async createDraftStockIn(stockInData) {
    try {
  return await http.post('/stock-in/v2/draft', stockInData);
    } catch (error) {
      console.error('Error creating draft stock in record:', error);
      throw error;
    }
  }

  // Get inventory overview
  static async getInventoryOverview(businessId) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      return await http.get(`/stock-in/v2/inventory/overview?businessId=${bId}&includeInactive=true`);
    } catch (error) {
      console.error('Error fetching inventory overview:', error);
      throw error;
    }
  }

  // Get specific stock in record
  static async getStockInById(id) {
    try {
  return await http.get(`/stock-in/v2/${id}`);
    } catch (error) {
      console.error('Error fetching stock in record:', error);
      throw error;
    }
  }

  // Convert draft to completed
  static async completeDraft(id) {
    try {
  return await http.put(`/stock-in/v2/${id}/complete`);
    } catch (error) {
      console.error('Error completing draft:', error);
      throw error;
    }
  }

  // Delete stock in record
  static async deleteStockIn(id) {
    try {
  return await http.delete(`/stock-in/v2/${id}`);
    } catch (error) {
      console.error('Error deleting stock in record:', error);
      throw error;
    }
  }

  // Revert any stock in record, in any order (admin PIN once a later TSR has consumed it)
  // @param {number} id - Stock-in record ID
  // @param {number[]} line_item_ids - Optional array of line_item_ids to selectively revert
  static async revertStockIn(id, line_item_ids = null, pin = null) {
    try {
      const payload = {};
      if (line_item_ids && line_item_ids.length > 0) {
        payload.line_item_ids = line_item_ids;
      }
      if (pin) {
        payload.pin = pin;
      }
      return await postUndo(`/stock-in/v2/${id}/revert`, payload || {});
    } catch (error) {
      console.error('Error reverting stock in record:', error);
      throw error;
    }
  }
}

export default StockInService;